import { z } from "zod"
import { type Article, articleSchema } from "@/models/article"
import { doGetRequest, generateImagesList } from "@/lib/utils.ts"

export const publicationSchema = z.object({
  id: z.number(),
  document_id: z.string().nullable().default(null),
  blob_id: z.string().nullable().default(null),
  issn: z.string().nullable().default(null),
  year: z.number(),
  number: z.string(),
  total_pages: z.number().nullable().default(null),
  cover: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  articles_count: z.number().optional()
})

export type Publication = z.infer<typeof publicationSchema>

export const getPublications = async ({ signal }: { signal: AbortSignal }): Promise<Publication[]> => {
  return doGetRequest("/api/publications", { signal }, (response:any) => {
    return response.data.map((publication: any) => publicationSchema.parse(publication))
  })
}

export const getLastPublication = async ({ signal }: { signal: AbortSignal }) => {
  return doGetRequest("/api/publications/lasts", { signal }, (response:any) => {
    return response.data.map((publication: any) => publicationSchema.parse(publication))
  })
}

export const getPublication = async ({
  id,
  signal
}: {
  id: string | undefined
  signal: AbortSignal
}): Promise<Publication> => {
  if (!id) throw new Error("Publication ID is required")

  return doGetRequest(`/api/publications/${id}`, { signal }, (response:any) => {
    return publicationSchema.parse(response.data)
  })
}

export const attachmentSchema = z.object({
  src: z.string(),
  w: z.number(),
  h: z.number()
})

export type Attachment = z.infer<typeof attachmentSchema>

export const getPublicationAttachments = async ({
  publicationId,
  documentId,
  signal
}: {
  publicationId: string | undefined | null
  documentId: string | undefined | null
  signal: AbortSignal
}): Promise<Attachment[]> => {
  if (!documentId) throw new Error("Document ID is required")

  return doGetRequest(`/zapdoc/api/documents/${documentId}/page_count`, { signal }, (response:any) => {

    doGetRequest('/api/update_stats/view', {
      signal,
      params: {
        type: 'publication',
        publication_id: publicationId
      }
    })

    let slides =       {
      src: `/publications/${publicationId}/cover`,
      w: 700,
      h: 990
    }

    return [
      slides,
      ...generateImagesList(documentId, response.data)
    ]
  })
}

export const getPublicationArticles = async ({
  id,
  signal
}: {
  id: string | undefined
  signal: AbortSignal
}): Promise<Article[]> => {
  if (!id) throw new Error("Publication ID is required")

  return doGetRequest(`/api/publications/${id}/articles`, { signal }, (response:any) => {
    return response.data.map((article: any) => articleSchema.parse(article))
  })
}
